import { Container, Typography } from "@material-ui/core"
import {
  AiBlessing,
  TagCategory,
  getAiImageGetAllBlessing,
  getAiImageGetAllTagCategory,
} from "api"
import BackButton from "components/BackButton/BackButton"
import { useGroupPurchasesContext } from "components/PurchasePage/GroupPurchasesContext"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { isDesktop } from "react-device-detect"
import styled from "styled-components"
import colors from "theme/colors"
import { v4 as uuidv4 } from "uuid"
import BackgroundsAi from "./BackgroundsAi"
import CategoriesAi from "./CategoriesAi"
import { useCreateAiContext } from "./CreateAiContext"
import CreatePictureAiButton from "./CreatePictureAiButton"
import PropertiesAi from "./PropertiesAi"
import SenderAi from "./SenderAi"
import StylesAi from "./StylesAi"

const StyledContainer = styled(Container)`
  padding: 94px 16px 80px 16px;

  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 120px 60px 80px 60px;
  }
`

const StyledBackButton = styled(BackButton)`
  margin-bottom: 24px;

  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 48px;
  }
`

const StyledTitle = styled(Typography).attrs({
  component: "h1",
  variant: "h1",
})`
  color: ${colors.black};
  white-space: nowrap;
  font-size: 40px;
  line-height: 0.6;
  font-family: Speedee, NarkisBlock, sans-serif;
  height: 40px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`
const StyledContent = styled.div`
  margin-bottom: 40px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 60px;
  }
`
const StyledExplanation = styled(Typography).attrs({
  variant: "body1",
})`
  font-size: 20px;
  font-weight: 100;
  color: #000;
  line-height: 0.92;
  text-align: right;
  margin-bottom: 4px;

  ${(p) => p.theme.breakpoints.down("sm")} {
    font-size: 18px;
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-bottom: 8px;
  }
`
const StyledInfo = styled(Typography).attrs({
  variant: "body2",
})`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.black};
  ${(p) => p.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const StyledError = styled(Typography).attrs({ variant: "body1" })`
  color: #d70202;
  font-size: 20px;
  text-align: right;
  line-height: 1.1;
  margin: 0px;
`

const CreateAiPage = () => {
  const {
    blessingId,
    postAiImage,
    setSessionId,
    stylesData,
    propertiesData,
    backgroundsData,
  } = useCreateAiContext()
  const [categories, setCategories] = useState<AiBlessing[]>()
  const [choicesTag, setChoicesTag] = useState<TagCategory[]>()
  const [properties, setProperties] = useState<TagCategory[]>()
  const [styles, setStyles] = useState<TagCategory[]>()
  const [backgrounds, setBackgrounds] = useState<TagCategory[]>()
  const [checkValiation, setCheckValidation] = useState(false)

  const { setPrevPath } = useGroupPurchasesContext()

  useEffect(() => {
    const getAllBlessingCategories = async () => {
      const res = await getAiImageGetAllBlessing()
      setCategories(res.data.body)
    }
    getAllBlessingCategories()
    createSessionId()

    setPrevPath("/create-ai")
  }, [])

  useEffect(() => {
    const getOtherData = async () => {
      const res = await getAiImageGetAllTagCategory(blessingId)
      setChoicesTag(res.data.body)
    }
    getOtherData()
  }, [blessingId])

  useEffect(() => {
    if (choicesTag) {
      setProperties(
        choicesTag?.filter((tag) => tag.tagCategoryType === "Clean")
      )
      setStyles(choicesTag?.filter((tag) => tag.tagCategoryType === "Square"))
      setBackgrounds(
        choicesTag?.filter((tag) => tag.tagCategoryType === "Image")
      )
    }
  }, [choicesTag])

  const createSessionId = () => {
    const sessionId = localStorage.getItem("sessionId")
    if (!sessionId) {
      createTimeStampAndSessionId()
    } else {
      setSessionId(sessionId)
      const storedTimestamp = new Date(
        JSON.parse(localStorage.getItem("timestamp"))
      )

      const timeDifference = new Date().getTime() - storedTimestamp.getTime()

      if (timeDifference >= 10800000) {
        createTimeStampAndSessionId()
      }
    }
  }

  const createTimeStampAndSessionId = () => {
    const generatedUUID = uuidv4()
    localStorage.setItem("sessionId", JSON.stringify(generatedUUID))
    const timestamp = new Date().toISOString()
    localStorage.setItem("timestamp", JSON.stringify(timestamp))
    setSessionId(JSON.stringify(generatedUUID))
  }
  const ref = useRef<HTMLButtonElement>(null)
  const stylesRef = useRef<HTMLInputElement>(null)
  const bgRef = useRef<HTMLInputElement>(null)

  const submitNewAiGiftCard = () => {
    setCheckValidation(true)
    propertiesData.length === 0
      ? ref?.current?.focus()
      : propertiesData.length > 0 && !stylesData
      ? stylesRef?.current?.focus()
      : propertiesData.length > 0 && !backgroundsData && bgRef?.current?.focus()
    if (stylesData && propertiesData.length > 0 && backgroundsData) {
      postAiImage()
    }
  }
  return (
    <StyledContainer>
      <StyledBackButton linkText="חזרה" prevPath="/" />
      <StyledContent>
        <StyledTitle>
          <Image
            src="/img/aiIcon.svg"
            width={isDesktop ? 40 : 30}
            height={isDesktop ? 40 : 30}
            alt=""
          />
          יצירת תמונה ב-AI
        </StyledTitle>
        <StyledExplanation>
          יצירת תמונה של מקדונלד&apos;ס בהתאמה אישית
        </StyledExplanation>
        <StyledInfo>
          <Image
            src="/img/icons/infoIcon.svg"
            width={isDesktop ? 20 : 14}
            height={isDesktop ? 20 : 14}
            alt="info-icon"
            aria-label="לתשומת ליבך, אפשרות ליצור תמונה עד 4 ניסיונות בלבד"
          />
          לתשומת ליבך, אפשרות ליצור תמונה עד 4 ניסיונות בלבד{" "}
        </StyledInfo>
      </StyledContent>

      {categories && <CategoriesAi categories={categories} />}
      {properties && (
        <PropertiesAi
          ref={propertiesData.length === 0 ? ref : null}
          properties={properties}
          checkValiation={checkValiation}
        />
      )}

      {styles && (
        <StylesAi
          ref={stylesRef}
          styles={styles}
          checkValiation={checkValiation}
        />
      )}
      {backgrounds && (
        <BackgroundsAi
          ref={bgRef}
          backgrounds={backgrounds}
          checkValiation={checkValiation}
        />
      )}
      <SenderAi />
      {(!stylesData || propertiesData.length === 0 || !backgroundsData) &&
        checkValiation && (
          <StyledError>יש שדה אחד או יותר שלא בחרת</StyledError>
        )}
      <CreatePictureAiButton
        submit={submitNewAiGiftCard}
        enabledButton={false}
        text="יצירת תמונה"
      />
    </StyledContainer>
  )
}

export default CreateAiPage
